/**
 * Wire Entry Page
 *
 * Page for recording wire transfers with recent transaction history.
 */

import React, { useState } from 'react';
import {
  Card,
  Table,
  Space,
  Typography,
  Tag,
  Button,
  message,
  Tooltip,
  Statistic,
  Row,
  Col,
  Segmented,
} from 'antd';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  ReloadOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { bankTransactionsApi } from '../services/api';
import WireEntryForm from '../components/financial/WireEntryForm';
import TransactionReviewPanel from '../components/financial/TransactionReviewPanel';
import { colors, spacing } from '../components/design-system/tokens';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const WireEntry: React.FC = () => {
  const [filter, setFilter] = useState<'all' | 'IN' | 'OUT'>('all');

  // Fetch recent wire transactions
  const { data: transactionsData, isLoading, refetch } = useQuery({
    queryKey: ['bank-transactions', 'wires', filter],
    queryFn: async () => {
      const params: any = {
        limit: 50,
      };
      if (filter !== 'all') {
        params.transactionType = filter === 'IN' ? 'WIRE_IN' : 'WIRE_OUT';
      }
      return bankTransactionsApi.list(params);
    },
  });

  // Filter only wire transactions - handle both AxiosResponse and direct data
  const rawData = (transactionsData as any)?.data || transactionsData;
  const transactions = (rawData?.transactions || []).filter(
    (t: any) => t.transactionType === 'WIRE_IN' || t.transactionType === 'WIRE_OUT'
  );

  // Calculate totals
  const totals = transactions.reduce(
    (acc: any, t: any) => {
      if (t.transactionType === 'WIRE_IN') {
        acc.incoming += Number(t.amount);
        acc.inCount++;
      } else {
        acc.outgoing += Number(t.amount);
        acc.outCount++;
      }
      return acc;
    },
    { incoming: 0, outgoing: 0, inCount: 0, outCount: 0 }
  );

  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 100,
      render: (date: string) => dayjs(date).format('MMM D, YYYY'),
      sorter: (a: any, b: any) => dayjs(a.date).unix() - dayjs(b.date).unix(),
      defaultSortOrder: 'descend' as const,
    },
    {
      title: 'Direction',
      dataIndex: 'transactionType',
      key: 'direction',
      width: 100,
      render: (type: string) =>
        type === 'WIRE_IN' ? (
          <Tag color="green" icon={<ArrowDownOutlined />}>
            IN
          </Tag>
        ) : (
          <Tag color="red" icon={<ArrowUpOutlined />}>
            OUT
          </Tag>
        ),
    },
    {
      title: 'Reference',
      dataIndex: 'reference',
      key: 'reference',
      width: 150,
      render: (ref: string) => <Text code>{ref}</Text>,
    },
    {
      title: 'Counterparty',
      dataIndex: 'counterparty',
      key: 'counterparty',
      width: 180,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      align: 'right' as const,
      render: (amount: number, record: any) => (
        <Text
          strong
          style={{
            color: record.transactionType === 'WIRE_IN' ? colors.status.positive : colors.status.negative,
          }}
        >
          {record.transactionType === 'WIRE_IN' ? '+' : '-'}$
          {Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
        </Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => {
        const statusColors: Record<string, string> = {
          UNRECONCILED: 'orange',
          PARTIAL: 'blue',
          RECONCILED: 'green',
          PUSHED: 'cyan',
        };
        return <Tag color={statusColors[status] || 'default'}>{status}</Tag>;
      },
    },
    {
      title: 'Allocations',
      dataIndex: 'allocations',
      key: 'allocations',
      width: 120,
      render: (allocations: any[]) => {
        if (!allocations || allocations.length === 0) {
          return <Text type="secondary">Unallocated</Text>;
        }
        return (
          <Tooltip
            title={allocations.map((a) => a.shipment?.reference || a.shipmentId).join(', ')}
          >
            <Tag color="blue" icon={<LinkOutlined />}>
              {allocations.length} shipment{allocations.length > 1 ? 's' : ''}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Notes',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (desc: string) =>
        desc ? <Text type="secondary">{desc}</Text> : <Text type="secondary">-</Text>,
    },
  ];

  const handleSuccess = () => {
    refetch();
    message.success('Wire recorded successfully');
  };

  return (
    <div style={{ padding: spacing.xl }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Title level={2} style={{ margin: 0 }}>
            Wire Transfers
          </Title>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
              Refresh
            </Button>
            <WireEntryForm onSuccess={handleSuccess} />
          </Space>
        </div>

        {/* Review Panel for flagged transactions */}
        <TransactionReviewPanel onResolve={() => refetch()} />

        {/* Summary Cards */}
        <Row gutter={16}>
          <Col span={8}>
            <Card>
              <Statistic
                title="Incoming (Received)"
                value={totals.incoming}
                precision={2}
                prefix="$"
                valueStyle={{ color: colors.status.positive }}
                suffix={
                  <Text type="secondary" style={{ fontSize: 14 }}>
                    ({totals.inCount} wires)
                  </Text>
                }
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic
                title="Outgoing (Sent)"
                value={totals.outgoing}
                precision={2}
                prefix="$"
                valueStyle={{ color: colors.status.negative }}
                suffix={
                  <Text type="secondary" style={{ fontSize: 14 }}>
                    ({totals.outCount} wires)
                  </Text>
                }
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic
                title="Net Position"
                value={totals.incoming - totals.outgoing}
                precision={2}
                prefix="$"
                valueStyle={{
                  color:
                    totals.incoming - totals.outgoing >= 0
                      ? colors.status.positive
                      : colors.status.negative,
                }}
              />
            </Card>
          </Col>
        </Row>

        {/* Transactions Table */}
        <Card
          title="Recent Wire Transfers"
          extra={
            <Segmented
              value={filter}
              onChange={(value) => setFilter(value as any)}
              options={[
                { label: 'All', value: 'all' },
                {
                  label: (
                    <Space>
                      <ArrowDownOutlined style={{ color: colors.status.positive }} />
                      Incoming
                    </Space>
                  ),
                  value: 'IN',
                },
                {
                  label: (
                    <Space>
                      <ArrowUpOutlined style={{ color: colors.status.negative }} />
                      Outgoing
                    </Space>
                  ),
                  value: 'OUT',
                },
              ]}
            />
          }
        >
          <Table
            columns={columns}
            dataSource={transactions}
            rowKey="id"
            loading={isLoading}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showTotal: (total) => `${total} wire transfers`,
            }}
            size="middle"
          />
        </Card>
      </Space>
    </div>
  );
};

export default WireEntry;
